import { ItemListadoServiciosGuarderia } from "../../../services/guarderiaServicios";
import { CiudadResumida, Sector } from "../../../services/paisesServicios";
import {
  DatosTransporte,
  NuevoAgendamientoGuarderia,
  PagoNuevoAgendamientoGuarderia,
} from "../../../services/agendamientosGuarderiaServicios";
import { ServicioTransporte } from "../../../services/transporteServicios";
import { MetodoPago } from "../../../services/ventasServicios";
import { parseCurrency } from "../../../utils/numberUtils";
import { obtenerStringDeFecha, ordenarFechas } from "../../../utils/dateUtils";
import { NuevoItemCarritoGuarderiaRequest } from "../../../services/carritoServicios";
import { RespuestaBuscarPropietario } from "../../../services/propietariosServicios";

export interface Model {
  idMascota: string;
  idPropietario: string;
  nombreMascota: string;
  nombresPropietario: string;
  datosPropietario?: RespuestaBuscarPropietario;
  diasIndividualesGuarderia: number;
  valorFinalDiaIndividual?: number;
  servicioDiaIndividual?: ItemListadoServiciosGuarderia;
  plan?: ItemListadoServiciosGuarderia;
  valorFinalPlan?: number;
  fechasProgramadas: Date[];
  contrataTransporte: boolean;
  ciudadOrigen?: CiudadResumida;
  contrataIda: boolean;
  contrataVuelta: boolean;
  direccionTransporte?: string;
  sectorTransporte?: Sector;
  fechasTransporte: Date[];
  tarifaIda?: ServicioTransporte;
  valorFinalIda?: number;
  tarifaVuelta?: ServicioTransporte;
  valorFinalVuelta?: number;
  tarifaIdaVuelta?: ServicioTransporte;
  valorFinalIdaVuelta?: number;
  usarTarifasPorTrayecto: boolean;
  descuento: number;
  total?: number;
  pago?: PagoModel;
}

export interface PagoModel {
  monto: string;
  metodo: MetodoPago;
}

export const defaultModel: Model = {
  idMascota: "",
  idPropietario: "",
  nombreMascota: "",
  nombresPropietario: "",
  fechasProgramadas: [],
  contrataTransporte: true,
  contrataIda: true,
  contrataVuelta: true,
  usarTarifasPorTrayecto: false,
  diasIndividualesGuarderia: 0,
  fechasTransporte: [],
  descuento: 0,
};

const soloContrataTransporteIda = (model: Model): boolean => {
  return (
    model.contrataIda && model.contrataTransporte && !model.valorFinalIdaVuelta
  );
};

const soloContrataTransporteVuelta = (model: Model): boolean => {
  return (
    model.contrataVuelta &&
    model.contrataTransporte &&
    !model.valorFinalIdaVuelta
  );
};

const soloContrataTransporteIdaVuelta = (model: Model): boolean => {
  return (
    !!model.tarifaIdaVuelta &&
    model.contrataIda &&
    model.contrataVuelta &&
    model.contrataTransporte
  );
};

export function buildNuevoAgendamiento(
  model: Model
): NuevoAgendamientoGuarderia {
  let fechasProgramadas = ordenarFechas(model.fechasProgramadas).map((f) =>
    obtenerStringDeFecha(f)
  );

  let fechasTransporte = ordenarFechas(model.fechasTransporte).map((f) =>
    obtenerStringDeFecha(f)
  );

  const datosTransporteIda: DatosTransporte | undefined =
    soloContrataTransporteIda(model)
      ? {
          idSector: model.sectorTransporte!.id,
          direccion: model.direccionTransporte!,
          idTarifa: model.tarifaIda!.id,
          precioFinal: model.valorFinalIda!,
          fechaInicio: fechasTransporte[0],
          fechaFin: fechasTransporte[fechasTransporte.length - 1],
          fechasProgramadas: fechasTransporte,
        }
      : undefined;

  const datosTransporteVuelta: DatosTransporte | undefined =
    soloContrataTransporteVuelta(model)
      ? {
          idSector: model.sectorTransporte!.id,
          direccion: model.direccionTransporte!,
          idTarifa: model.tarifaVuelta!.id,
          precioFinal: model.valorFinalVuelta!,
          fechaInicio: fechasTransporte[0],
          fechaFin: fechasTransporte[fechasTransporte.length - 1],
          fechasProgramadas: fechasTransporte,
        }
      : undefined;

  const datosTransporteIdaVuelta: DatosTransporte | undefined =
    soloContrataTransporteIdaVuelta(model)
      ? {
          idSector: model.sectorTransporte!.id,
          direccion: model.direccionTransporte!,
          idTarifa: model.tarifaIdaVuelta!.id,
          precioFinal: model.valorFinalIdaVuelta!,
          fechaInicio: fechasTransporte[0],
          fechaFin: fechasTransporte[fechasTransporte.length - 1],
          fechasProgramadas: fechasTransporte,
        }
      : undefined;

  const pago: PagoNuevoAgendamientoGuarderia | undefined = model.pago
    ? {
        monto: parseCurrency(model.pago!.monto, "$", ","),
        metodo: model.pago!.metodo,
      }
    : undefined;

  return {
    idMascota: model.idMascota,
    datosReservaGuarderia: {
      fechaInicio: fechasProgramadas[0],
      fechaFin: fechasProgramadas[fechasProgramadas.length - 1],
      fechasProgramadas: fechasProgramadas,
      idPlan: model.plan?.id,
      precioFinalPlan: model.valorFinalPlan,
      diasIndividuales: !!model.diasIndividualesGuarderia
        ? model.diasIndividualesGuarderia
        : undefined,
      idServicioDiaIndividual: model.servicioDiaIndividual?.id,
      precioFinalDiaIndividual: model.valorFinalDiaIndividual,
    },
    datosTransporteIda,
    datosTransporteVuelta,
    datosTransporteIdaVuelta,
    descuento: model.descuento,
    pago,
  };
}

const validarModelParaNuevoItemCarritoGuarderia = (model: Model) => {
  if (model.plan && !model.valorFinalPlan) {
    throw Error("Plan debe tener un valor final");
  }

  if (model.diasIndividualesGuarderia && !model.diasIndividualesGuarderia) {
    throw Error("Dias individuales deben tener un servicio asociado");
  }

  if (model.diasIndividualesGuarderia && !model.valorFinalDiaIndividual) {
    throw Error("Dias individuales deben tener un servicio asociado");
  }
};

export function buildNuevoItemCarritoGuarderia(
  model: Model
): NuevoItemCarritoGuarderiaRequest {
  validarModelParaNuevoItemCarritoGuarderia(model);

  let fechasProgramadas = ordenarFechas(model.fechasProgramadas);

  const datosPlan = model.plan
    ? { idPlan: model.plan.id, precioFinal: model.valorFinalPlan!! }
    : undefined;

  const datosDiasIndividuales = model.diasIndividualesGuarderia
    ? {
        diasIndividuales: model.diasIndividualesGuarderia,
        idServicio: model.servicioDiaIndividual!!.id,
        precioFinal: model.valorFinalDiaIndividual!!,
      }
    : undefined;

  const datosTransporteIda = soloContrataTransporteIda(model)
    ? {
        idTarifa: model.tarifaIda?.id!!,
        precioFinal: model.valorFinalIda!!,
      }
    : undefined;

  const datosTransporteVuelta = soloContrataTransporteVuelta(model)
    ? {
        idTarifa: model.tarifaVuelta?.id!!,
        precioFinal: model.valorFinalVuelta!!,
      }
    : undefined;

  const datosTransporteIdaVuelta = soloContrataTransporteIdaVuelta(model)
    ? {
        idTarifa: model.tarifaIdaVuelta?.id!!,
        precioFinal: model.valorFinalIdaVuelta!!,
      }
    : undefined;

  const datosTransporte = model.contrataTransporte
    ? {
        fechas: ordenarFechas(model.fechasTransporte).map((f) =>
          obtenerStringDeFecha(f)
        ),
        direccion: model.direccionTransporte!!,
        idSector: model.sectorTransporte?.id!!,
        datosIda: datosTransporteIda,
        datosVuelta: datosTransporteVuelta,
        datosIdaVuelta: datosTransporteIdaVuelta,
      }
    : undefined;

  return {
    idPropietario: model.idPropietario,
    tipoServicio: "GUARDERIA",
    idMascota: model.idMascota,
    datosReserva: {
      fechasProgramadas: fechasProgramadas.map((f) => obtenerStringDeFecha(f)),
      datosPlan,
      datosDiasIndividuales,
    },
    datosTransporte,
  };
}
